import { useState, useEffect, useMemo } from 'react'
import styles from './Connections.module.css'
import { useNavigate, useLocation } from 'react-router-dom'
import { UserRole } from '../../enums/userRole'
import { hasMinRole } from '../../lib/permissions'
import { useAuthStore, useConnectionsStore } from '../../store/appStore'
import CreateConnectionModal from './CreateConnectionModal'
import DuplicateConnectionModal from './DuplicateConnectionModal'
import { getTypeMetaByConnectionConfigId, getTypeMeta } from '../../utils/utils'
import { configApi } from '../../lib/apiClient'
import { toast } from '../../lib/toast'
import { Copy, SquarePen, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react'
import { Environment } from '../../types/environment'
import InputModal from '../../ui/inputModal/InputModal'
import { TagsEnv } from '../../ui/Tags/TagsEnv'

export default function Connections() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateConnectionId, setDuplicateConnectionId] = useState<string | null>(null);
  const [duplicateConnectionName, setDuplicateConnectionName] = useState('');
  const [duplicateConnectionEnv, setDuplicateConnectionEnv] = useState<Environment | ''>('');
  const [itemToDelete, setItemToDelete] = useState<{ id: string; title: string; isBase?: boolean } | null>(null);
  const [deletingConnectionId, setDeletingConnectionId] = useState<string | null>(null);
  const [togglingConnector, setTogglingConnector] = useState<string | null>(null);
  const user = useAuthStore(state => state.user)
  const connectors = useConnectionsStore(state => state.connectors)
  const { getConnections, addConnection, removeConnection, setConnectors, updateConnection } = useConnectionsStore(state => state)

  useEffect(() => {
    getConnections().then(setConnectors);
  }, []);

  useEffect(() => {
    if (!user || !hasMinRole(user?.role?.id, UserRole.Admin)) {
      navigate('/');
    }
  }, [user])

  const items = useMemo(() => {
    return (connectors || [])
      .map((c: any) => {
        let meta;
        if (c.connectionTypeId) {
          meta = getTypeMeta(c.connectionTypeId);
        } else {
          meta = getTypeMetaByConnectionConfigId(c.id, connectors);
        }
        return {
          icon: meta?.icon,
          id: c?.id,
          title: c?.connectionName || meta?.title,
          description: c?.description || meta?.description,
          route: meta?.route,
          environment: c?.environment,
          isBase: c?.isBase,
          isActive: c?.isActive,
        };
      })
      .filter(Boolean) as Array<{ icon: string; id: string; title: string; description: string; route: string; environment?: Environment; isBase?: boolean; isActive: boolean }>;
  }, [connectors]);

  const showList = location.pathname === '/connectors';

  const handleDuplicate = (connectionId: string, connectionName: string, environment?: Environment) => {
    setDuplicateConnectionId(connectionId);
    setDuplicateConnectionName(`${connectionName}`);
    setDuplicateConnectionEnv(environment || '');
    setShowDuplicateModal(true);
  };

  const handleConfirmDuplicate = async (name: string, environment: Environment | undefined) => {
    if (!duplicateConnectionId) return;
    try {
      const duplicated = await configApi.duplicate(duplicateConnectionId, name, environment || undefined);
      addConnection(duplicated);
      toast.success('Connection duplicated successfully');
      const updatedConnections = await getConnections();
      setConnectors(updatedConnections);
    } catch (error: any) {
      toast.error(`Failed to duplicate connection: ${error.message}`);
    } finally {
      setShowDuplicateModal(false);
      setDuplicateConnectionId(null);
    }
  };

  const isAdmin = user && hasMinRole(user?.role?.id, UserRole.Admin);
  const isSuperAdmin = user && hasMinRole(user?.role?.id, UserRole.SuperAdmin);

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      setTogglingConnector(id);
      await configApi.setActive(id, !currentActive);
      const existing = connectors.find(c => c.id === id);
      if (existing) updateConnection({ ...existing, isActive: !currentActive });
      toast.success(`Connector ${!currentActive ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      toast.error(`Failed: ${err.message}`);
    } finally {
      setTogglingConnector(null);
    }
  };

  const handleDeleteConnection = (item: { id: string; title: string; isBase?: boolean }) => {
    if (item.isBase) return;
    setItemToDelete(item);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete || deletingConnectionId === itemToDelete.id) return;
    const target = itemToDelete;
    try {
      setDeletingConnectionId(target.id);
      await configApi.delete(target.id);
      removeConnection(target.id);
      toast.success(`Connector "${target.title}" deleted successfully`);
    } catch (error: any) {
      toast.error(`Failed to delete connector: ${error.message}`);
    } finally {
      setDeletingConnectionId(null);
      setItemToDelete(null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <h1>Connectors</h1>
        </div>
        <p>Select a connection type to configure</p>
      </div>

      {showCreateModal && (
        <CreateConnectionModal
          onClose={() => setShowCreateModal(false)}
          onCreate={(newConnection: any) => {
            useConnectionsStore.getState().addConnection(newConnection);
            setShowCreateModal(false);
          }}
        />
      )}

      {showList && (
        <div className={styles.grid}>
          {items.map(item => (
            <div
              key={item.id}
              className={styles.gridCard}
              onClick={() => navigate(`${item.route}/${item.id}`)}
            >
              {isSuperAdmin && (
                //todo- change it to a custom toggle
                // <div
                //   className={styles.gridToggleAction}
                //   onClick={(e) => e.stopPropagation()}
                //   title={item.isActive ? 'Disable connector' : 'Enable connector'}
                // >
                //   <ToggleSwitch
                //     checked={item.isActive ?? true}
                //     onChange={() => handleToggleActive(item.id, item.isActive ?? true)}
                //     disabled={togglingConnector === item.id}
                //   />
                // </div>

                <button
                  className={`${styles.statusBadge} ${item.isActive ? styles.statusActive : styles.statusInactive}`}
                  onClick={(e) => {e.preventDefault(); e.stopPropagation(); handleToggleActive(item.id, item.isActive ?? true)}}
                  disabled={togglingConnector === item.id}
                  title={item.isActive ? 'Active - click to deactivate' : 'Inactive - click to activate'}
                >
                  {item.isActive ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  {item.isActive ? 'Active' : 'Inactive'}
                </button>
              )}
              <div className={styles.gridCardActions}>
                <button
                  className={styles.gridActionBtn}
                  onClick={(e) => { e.stopPropagation(); navigate(`${item.route}/${item.id}`); }}
                  title="Edit Connection"
                >
                  <SquarePen size={14} />
                </button>
                {isAdmin && (
                  <button
                    className={styles.gridActionBtn}
                    onClick={(e) => { e.stopPropagation(); handleDuplicate(item.id, item.title, item.environment); }}
                    title="Duplicate Connection"
                  >
                    <Copy size={14} />
                  </button>
                )}
                {isAdmin && !item.isBase && (
                  <button
                    className={`${styles.gridActionBtn} ${styles.gridActionBtnDanger}`}
                    onClick={(e) => { e.stopPropagation(); handleDeleteConnection(item); }}
                    title="Delete Connection"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              {item.icon && <img src={item.icon} alt={item.title} className={styles.gridCardIcon} />}
              <div className={styles.gridCardTitle}>
                {item.title}
                {item.isBase && <span className={styles.baseBadge}>Base</span>}
              </div>
              <div className={styles.gridCardDescription}>{item.description}</div>
              <div className={styles.gridCardFooter}>
                <TagsEnv env={item.environment as 'test' | 'dev' | 'prod' | undefined} isToolDisabledByConnector={!item || !item.environment} />
              </div>
            </div>
          ))}
        </div>
      )}

      <InputModal
        open={itemToDelete !== null}
        title="Delete Connector"
        showInput={false}
        danger
        message={`Delete connector "${itemToDelete?.title}"? Tools using this connector will be unlinked.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={itemToDelete !== null && deletingConnectionId === itemToDelete.id}
        loadingText="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deletingConnectionId) {
            setItemToDelete(null);
          }
        }}
      />

      <DuplicateConnectionModal
        open={showDuplicateModal}
        title="Duplicate Connection"
        initialName={duplicateConnectionName}
        initialEnvironment={duplicateConnectionEnv}
        confirmText="Duplicate"
        cancelText="Cancel"
        onConfirm={(name, environment) => handleConfirmDuplicate(name, (environment || undefined) as 'test' | 'prod' | 'dev' | undefined)}
        onCancel={() => {
          setShowDuplicateModal(false);
          setDuplicateConnectionId(null);
        }}
      />
    </div>
  )
}
